import { useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./BaseModal.module.css";
import { X } from "lucide-react";

/* How many modals are open.

   The background scroll lock is one class on one <body>, but modals stack: the
   FAQ editor opens the category manager over itself, and the category manager
   opens a delete confirmation over that. Each used to add and remove the class
   outright, so closing the TOP one unlocked the page while the one underneath
   was still open, and the background scrolled behind it. Counting means the
   class goes on with the first and comes off with the last. */
let openModals = 0;
const lockScroll = () => {
  openModals += 1;
  document.body.classList.add("modal-open");
};
const unlockScroll = () => {
  openModals = Math.max(0, openModals - 1);
  if (openModals === 0) document.body.classList.remove("modal-open");
};

const BaseModal = ({
  open,
  onClose,
  title,
  children,
  footer,
  width = "600px",
  style: modalStyle = {},
}) => {
  useEffect(() => {
    if (!open) return undefined;
    lockScroll();
    return unlockScroll;
  }, [open]);

  if (!open) return null;

  // Render into document.body via a portal so the fixed overlay is positioned
  // relative to the viewport — NOT to any ancestor that has a `transform`
  // (e.g. cards with `:hover { transform }`), which would otherwise become the
  // containing block and make the modal jump/flicker as the cursor moves.
  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        style={{ width, ...modalStyle }}
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h3>{title}</h3>
          <button onClick={onClose} className={styles.closeBtn}>
            <X size={18} />
          </button>
        </div>

        <div className={styles.body}>
          {children}
        </div>

        {footer && (
          <div className={styles.footer}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default BaseModal;
